import { getEnvFromHost } from './constants';

export interface CreateObjectKeyOptions {
    env?: string;
    companyId: string;
    type: string;
    prefix?: string;
    includeRandom?: boolean;
    timestamp?: number;
}

/**
 * Creates a unique object key for file uploads to S3
 *
 * @param fileName - The original filename
 * @param options - Configuration options
 * @returns Formatted object key string
 *
 * @example
 * // For paylater/invoice uploads
 * createObjectKey('invoice.pdf', {
 *   companyId: 'company123',
 *   type: 'invoice',
 *   prefix: 'invoice',
 *   includeRandom: true
 * });
 * // Returns: "prod/company123/invoice/invoice_1234567890_invoice_abc123.pdf"
 *
 * @example
 * // For general file uploads
 * createObjectKey('document.pdf', {
 *   companyId: 'company123',
 *   type: 'bank',
 *   prefix: 'bank'
 * });
 * // Returns: "prod/company123/bank/bank_1234567890_document.pdf"
 */
export function createObjectKey(fileName: string, options: CreateObjectKeyOptions): string {
    const {
        env = getEnvFromHost(window.location.hostname),
        companyId,
        type,
        prefix = type,
        includeRandom = false,
        timestamp = new Date().valueOf(),
    } = options;

    // Extract file extension and name
    const ext = fileName.split('.').pop() || '';
    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
    const sanitizedName = sanitizeFileName(nameWithoutExt);

    // Build the filename parts
    const parts = [prefix, timestamp.toString(), sanitizedName];

    // Add random string if requested (for extra uniqueness)
    if (includeRandom) {
        const random = Math.random().toString(36).substring(2, 8);
        parts.push(random);
    }

    const finalFilename = parts.join('_') + (ext ? `.${ext}` : '');

    // Return raw S3 key; callers should URL-encode when using in requests
    return `${env}/${companyId}/${type}/${finalFilename}`;
}

/**
 * Convenience function for paylater invoice uploads
 * Maintains compatibility with existing paylater code
 */
export function createInvoiceObjectKey(fileName: string, companyId: string): string {
    return createObjectKey(fileName, {
        companyId,
        type: 'invoice',
        prefix: 'invoice',
        includeRandom: true,
    });
}

/**
 * Convenience function for general file uploads
 * Compatible with existing file-upload feature
 */
export function createFileObjectKey(fileName: string, companyId: string, type: string): string {
    return createObjectKey(fileName, {
        companyId,
        type,
        prefix: type,
        includeRandom: false,
    });
}

/**
 * Truncates a file name to a specified length
 *
 * @param fileName - The original filename
 * @param maxLength - The maximum length of the filename
 * @returns The truncated filename
 */
export function getTruncatedFileName(fileName: string, maxLength: number = 10) {
    // Determine base name and extension reliably (ignore leading dot files)
    const lastDotIndex = fileName.lastIndexOf('.');
    const hasExtension = lastDotIndex > 0 && lastDotIndex < fileName.length - 1;
    const baseName = hasExtension ? fileName.slice(0, lastDotIndex) : fileName;
    const fileExtension = hasExtension ? fileName.slice(lastDotIndex + 1) : '';

    if (baseName.length <= maxLength) {
        return fileExtension ? `${baseName}.${fileExtension}` : baseName;
    }

    // Truncate in the middle with a single ellipsis character
    const ellipsis = '…';
    const startLength = Math.ceil((maxLength - 1) / 2);
    const endLength = Math.floor((maxLength - 1) / 2);
    const start = baseName.slice(0, Math.max(0, startLength));
    const end = baseName.slice(Math.max(baseName.length - endLength, 0));
    const truncated = `${start}${ellipsis}${end}`;

    return fileExtension ? `${truncated}.${fileExtension}` : truncated;
}

function sanitizeFileName(input: string): string {
    // Normalize Unicode and strip diacritics
    const normalized = input.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    // Split ext safely
    const lastDot = normalized.lastIndexOf('.');
    const base = lastDot > 0 ? normalized.slice(0, lastDot) : normalized;
    const ext = lastDot > 0 ? normalized.slice(lastDot + 1) : '';

    const safeBase = base
        .replace(/[\s#?&+%/\\:;'"<>|`~^]/g, '_') // reserved/awkward chars -> _
        .replace(/[^A-Za-z0-9._-]/g, '_') // anything else non-URL-safe -> _
        .replace(/_+/g, '_') // collapse repeats
        .replace(/^_+|_+$/g, ''); // trim

    const safeExt = ext.replace(/[^A-Za-z0-9]/g, '').slice(0, 16); // conservative ext

    return safeExt ? `${safeBase}.${safeExt}` : safeBase;
}
